import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  FieldOption,
  FieldValue,
  FieldValueType,
  StatusGroup,
  TaskType,
} from 'api/tasks';
import { StatusPicker } from 'components/atoms/status-picker';
import { RbacStatusPickerTrigger } from 'components/organisms/rbac/components/rbac-status-picker';
import { ClassNamePropsWithChildren } from 'components/ozone-component';
import {
  useSelectedStatusOption,
  useStatusField,
} from 'data/fields/hooks/account-fields';
import {
  UpdateTaskActionPayloadOptions,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { useProjectAsRBACResource } from 'hooks/rbac/useWithAccess';

interface TaskStatusPickerProps extends ClassNamePropsWithChildren {
  taskId: number;
  projectId: number;
  type: TaskType;
  progress: number;
  fields: FieldValue<FieldValueType>[];
  size?: SizeType;
  disabled?: boolean;
  onChange?: (statusValue: number, statusFieldId: number) => void;
  options?: UpdateTaskActionPayloadOptions;
}

export const TaskStatusPicker: FC<TaskStatusPickerProps> = (props) => {
  const {
    taskId,
    projectId,
    type,
    progress,
    fields,
    size,
    disabled,
    onChange,
    options,
  } = props;

  const dispatch = useDispatch();
  const statusField = useStatusField();
  const selectedStatusOption = useSelectedStatusOption(fields);

  const projectResource = useProjectAsRBACResource(projectId);

  const handleStatusChange = (selected: FieldOption<StatusGroup>) => {
    if (statusField) {
      dispatch(
        updateTaskSagaAction({
          projectId,
          taskId,
          data: {
            fields: [
              {
                fieldId: statusField.fieldId,
                fieldValue: selected.value,
              },
            ],
          },
          options,
        }),
      );

      if (onChange) {
        onChange(selected.value, statusField.fieldId);
      }
    }
  };

  return (
    <>
      {selectedStatusOption && (
        <StatusPicker
          onChange={handleStatusChange}
          field={statusField}
          value={selectedStatusOption.value}
          dropdownHeader={<FormattedMessage id="task_status" />}
          type={type}
        >
          <RbacStatusPickerTrigger
            shape="round"
            selectedStatus={selectedStatusOption}
            progress={progress}
            size={size}
            disabled={disabled}
            rule="TASK_STATUS:WRITE"
            resourceData={projectResource}
            propsIfNoAccess={{
              disabled: true,
            }}
          />
        </StatusPicker>
      )}
    </>
  );
};
